import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CreativeNodeFlow from './CreativeNodeFlow';
import ErrorBoundary from './components/ErrorBoundary';
import AlertProvider from './components/AlertProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CreditsProvider } from './contexts/CreditsContext';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import { SubscriptionPlans, SubscriptionSuccess } from './components/subscription';
import MemberCreditsPage from './pages/MemberCreditsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AgentSettingsPage from './pages/AgentSettingsPage';
import ApiKeysSettingsPage from './pages/ApiKeysSettingsPage';
import PublishViewPage from './pages/PublishViewPage';
import PublishesListPage from './pages/PublishesListPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { useAdmin } from './hooks/useAdmin';
import CookieConsentBanner from './components/CookieConsentBanner';

// Uncomment the line below for environment debugging
// import EnvDiagnostic from './components/EnvDiagnostic';
// import EnvironmentDebugger from './components/EnvironmentDebugger';

/**
 * Home Route - shows LandingPage for unauthenticated, Dashboard for authenticated
 */
const HomeRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  return <Dashboard />;
};

/**
 * Protected Route wrapper - redirects to home if not authenticated
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Admin Route wrapper - redirects to home if user is not admin
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { isAdmin, isLoading } = useAdmin();

  if (loading || isLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Main App Content - handles routing
 */
const AppContent: React.FC = () => {
  return (
    <Routes>
      {/* Home route - WelcomeScreen or Dashboard based on auth */}
      <Route path="/" element={<HomeRoute />} />
      
      {/* New flow route */}
      <Route path="/flow" element={
        <ProtectedRoute>
          <CreativeNodeFlow />
        </ProtectedRoute>
      } />
      
      {/* Flow route with ID */}
      <Route path="/knod/:flowId" element={
        <ProtectedRoute>
          <CreativeNodeFlow />
        </ProtectedRoute>
      } />

      {/* Subscription routes */}
      <Route path="/pricing" element={<SubscriptionPlans />} />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      
      {/* Member & Credits Management */}
      <Route path="/member-credits" element={
        <ProtectedRoute>
          <MemberCreditsPage />
        </ProtectedRoute>
      } />

      {/* Agent Settings */}
      <Route path="/settings/agents" element={
        <ProtectedRoute>
          <AgentSettingsPage />
        </ProtectedRoute>
      } />

      {/* BYOK API Keys Settings */}
      <Route path="/settings/keys" element={
        <ProtectedRoute>
          <ApiKeysSettingsPage />
        </ProtectedRoute>
      } />

      {/* Publishes index — list of all the user's publishes */}
      <Route path="/publishes" element={
        <ProtectedRoute>
          <PublishesListPage />
        </ProtectedRoute>
      } />

      {/* Public Publish view — no auth wrapper; component handles unlock gates internally */}
      <Route path="/p/:slug" element={<PublishViewPage />} />

      {/* Password reset landing — user arrives here after clicking the email recovery link */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Admin dashboard */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboardPage />
        </AdminRoute>
      } />
      
      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // More comprehensive ResizeObserver error suppression
    const suppressResizeObserverErrors = () => {
      const resizeObserverErr = /ResizeObserver loop completed with undelivered notifications/;

      // Override window.onerror
      const originalOnError = window.onerror;
      window.onerror = (message: string | Event, source?: string, lineno?: number, colno?: number, error?: Error): boolean => {
        if (typeof message === 'string' && resizeObserverErr.test(message)) {
          return true; // Suppress the error
        }
        if (originalOnError) {
          return originalOnError(message, source, lineno, colno, error);
        }
        return false;
      };

      // Override addEventListener for error events
      const originalAddEventListener = EventTarget.prototype.addEventListener;
      EventTarget.prototype.addEventListener = function(
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions
      ): void {
        if (type === 'error') {
          const wrappedListener = function(this: EventTarget, event: Event): void {
            if (event instanceof ErrorEvent && event.error && resizeObserverErr.test(event.error.message)) {
              event.preventDefault();
              return;
            }
            if (typeof listener === 'function') {
              listener.call(this, event);
            } else {
              listener.handleEvent.call(this, event);
            }
          };
          return originalAddEventListener.call(this, type, wrappedListener as EventListener, options);
        }
        return originalAddEventListener.call(this, type, listener as EventListener, options);
      };

      return () => {
        window.onerror = originalOnError;
        EventTarget.prototype.addEventListener = originalAddEventListener;
      };
    };

    const cleanup = suppressResizeObserverErrors();
    return cleanup;
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CreditsProvider>
          <AlertProvider>
            <div className="app-container">
              <ErrorBoundary>
                {/* <EnvironmentDebugger /> */}
                {/* <EnvDiagnostic /> */}
                <AppContent />
                <CookieConsentBanner />
              </ErrorBoundary>
            </div>
          </AlertProvider>
        </CreditsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
